import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import '../styles/readiness.css';
import { readinessActions as A } from '../store/readinessSlice';
import { useReadinessController } from '../components/readiness/useReadinessController';
import ReadinessHeader from '../components/readiness/ReadinessHeader';
import ChatThread from '../components/readiness/ChatThread';
import Composer from '../components/readiness/Composer';
import ScoreRail from '../components/readiness/ScoreRail';
import ResultsScreen from '../components/readiness/ResultsScreen';
import LenderMatchesScreen from '../components/readiness/LenderMatchesScreen';
import ReviewScreen from '../components/readiness/ReviewScreen';
import ComplianceFooter from '../components/readiness/ComplianceFooter';
import ReadinessModals from '../components/readiness/ReadinessModals';
import { visibleQuestions } from '../config/readinessQuestions';
import { buildAppSections } from '../config/readinessAppSections';
import { scoreBreakdown, assessmentProgress } from '../utils/readinessAssessment';

/**
 * `/readiness` — the Owen borrower readiness experience:
 * chat assessment → live score → results → email-gated full application →
 * anonymous lender matches. Layout: header (60px) → main → compliance footer.
 */
export default function ReadinessPage() {
  const controller = useReadinessController();
  const dispatch = useDispatch();
  const state = useSelector((s) => s.readiness);

  // Arriving via a marketing-page "Sign in" link (?signin=1) opens the
  // passwordless email-code modal. There is deliberately no session
  // auto-restore: given the sensitivity of the data borrowers enter here,
  // leaving the session or closing the window requires a fresh code.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('signin') !== '1') return;
    if (!state.account) dispatch(A.openSignin());
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const {
    msgs, typing, answers, qi, done, matches, review, appMode, appDone, si, sections,
    selectedProgram,
  } = state;

  // Desktop breakpoint (score rail ≥1024px; mobile shows header pill)
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia('(min-width: 1024px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ── Derived state ───────────────────────────────────────────
  const programMatched = done || appMode || appDone;
  const breakdown = useMemo(
    () => scoreBreakdown(answers, { done: programMatched, sections }),
    [answers, programMatched, sections],
  );
  const { score, scoreColor, scoreLabel, cats } = breakdown;

  const vis = visibleQuestions(answers);
  const q = qi >= 0 ? vis[qi] : null;
  const secs = useMemo(
    () => (appMode || appDone ? buildAppSections(answers, selectedProgram) : []),
    [appMode, appDone, answers, selectedProgram],
  );
  const activeSection = appMode && si >= 0 ? secs[si] : null;
  const composerActive = (q !== null || activeSection !== null) && !typing && !done;

  const { answered, total } = assessmentProgress(answers);
  const savedSections = Object.keys(sections).length;
  const progress = appMode
    ? {
        label: si >= 0 ? `Section ${si + 1} of ${secs.length}` : 'Full application',
        pct: Math.round((savedSections / Math.max(secs.length, 1)) * 100) + '%',
      }
    : { label: `${answered} of ${total}`, pct: Math.round((answered / total) * 100) + '%' };

  const showChat = !done;

  return (
    <div className="lk-readiness">
      <ReadinessHeader
        progress={progress}
        score={score}
        scoreColor={scoreColor}
        showProgress={!done}
        showHeaderScore={!isDesktop && !done}
      />

      {showChat && (
        <main data-screen-label="Readiness chat" className="rdz-chat-main">
          <div className="rdz-chat-col">
            <div className="rdz-chat-scroll" ref={controller.chatElRef}>
              <ChatThread msgs={msgs} typing={typing} />
            </div>
            <div className="rdz-composer-wrap" ref={controller.composerWrapRef}>
              {composerActive && (
                <Composer
                  q={q}
                  activeSection={activeSection}
                  sectionCount={secs.length}
                  controller={controller}
                />
              )}
            </div>
          </div>
          {isDesktop && (
            <ScoreRail
              score={score}
              scoreColor={scoreColor}
              scoreLabel={scoreLabel}
              cats={cats}
              answers={answers}
            />
          )}
        </main>
      )}

      {done && review && <ReviewScreen controller={controller} />}
      {done && !review && !matches && <ResultsScreen breakdown={breakdown} controller={controller} />}
      {done && !review && matches && <LenderMatchesScreen />}

      <ComplianceFooter />
      <ReadinessModals score={score} controller={controller} />
    </div>
  );
}
